import { ReactElement, useCallback } from "react";
import { useRenderManager } from "../useRenderManager";
import { OneByOneTileContent } from "./OneByOneTileContent";
import { TwoByTwoTileContent } from "./TwoByTwoTileContent";
import { navigateToPeopleApp } from "client_legacy/Apps/appNavigation";
import { useRegisterTileUsage } from "./useRegisterTileUsage";
import { TileRenderState } from "../RenderState/common";
import { AppType } from "client/Api/Apps/common";
import { useBaseNavigation } from "./useInvokeNavigation";
import { TileContextProvider } from "../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type SkypeTileContentProps = {
	renderState: TileRenderState<AppType.Office365PeopleScreen>;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const PeopleTileContent = (props: SkypeTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu
	} = props;

	const [navigationElement, navigateHandler] = usePeopleTileNavigation(renderState, onEdit);

	const { dimension } = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<TwoByTwoTileContent
				onClick={navigateHandler}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
PeopleTileContent.displayName = "PeopleTileContent";

export function usePeopleTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.Office365PeopleScreen>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useCallback(
			(): void => {
				navigateToPeopleApp(
					() => registerTileUsage()
				);
			},
			[registerTileUsage]
		)
	);
}
